/** Workspace-scoped last-known Provider display names (UI only).
 * DISPLAY CACHE ≠ execution authority — canonical Provider API still gates runs. */

const STORAGE_KEY_PREFIX = 'vistoera.canvas.providerDisplay.v1'

export function providerDisplayCacheStorageKey(workspaceId: string): string {
  return `${STORAGE_KEY_PREFIX}.${workspaceId}`
}

type CacheMap = Record<string, string>

function readMap(workspaceId: string): CacheMap {
  if (!workspaceId || typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(providerDisplayCacheStorageKey(workspaceId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return {}
    const out: CacheMap = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof k === 'string' && typeof v === 'string' && v.trim()) out[k] = v.trim()
    }
    return out
  } catch {
    return {}
  }
}

function writeMap(workspaceId: string, map: CacheMap): void {
  if (!workspaceId || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(providerDisplayCacheStorageKey(workspaceId), JSON.stringify(map))
  } catch {
    // Quota / private mode — ignore; node-persisted label may still apply.
  }
}

export function readProviderDisplayNameFromCache(
  workspaceId: string,
  providerOptionId: string | null | undefined,
): string | null {
  const id = typeof providerOptionId === 'string' ? providerOptionId.trim() : ''
  if (!id || !workspaceId) return null
  return readMap(workspaceId)[id] ?? null
}

/** Write only when the display name actually changes (avoid render-loop writes). */
export function writeProviderDisplayNameToCache(
  workspaceId: string,
  providerOptionId: string,
  displayName: string,
): void {
  const id = providerOptionId.trim()
  const name = displayName.trim()
  if (!workspaceId || !id || !name) return
  const map = readMap(workspaceId)
  if (map[id] === name) return
  map[id] = name
  writeMap(workspaceId, map)
}

export function writeProviderDisplayNamesBulk(
  workspaceId: string,
  entries: Array<{ providerOptionId: string; displayName: string }>,
): void {
  if (!workspaceId || !entries.length) return
  const map = readMap(workspaceId)
  let changed = false
  for (const row of entries) {
    const id = row.providerOptionId.trim()
    const name = row.displayName.trim()
    if (!id || !name) continue
    if (map[id] !== name) {
      map[id] = name
      changed = true
    }
  }
  if (changed) writeMap(workspaceId, map)
}

export function removeProviderDisplayNameFromCache(workspaceId: string, providerOptionId: string): void {
  const id = providerOptionId.trim()
  if (!workspaceId || !id) return
  const map = readMap(workspaceId)
  if (!(id in map)) return
  delete map[id]
  writeMap(workspaceId, map)
}
